import { useState } from 'react';
import { Plane, Vector2, Vector3 } from 'three';

import { useCursor } from '@react-three/drei';
import { ThreeEvent, useThree } from '@react-three/fiber';
import { useEventCallback } from '@spazious/react-hooks';
import { useDrag } from '@use-gesture/react';

export interface SphereControlHandleProps {
  outline: boolean;
  color: string;
  size?: number;
  hoverCursor?: string;
  onClick?: (event: ThreeEvent<MouseEvent>) => void;
  onDrag?: (payload: { position: Vector2; last: boolean; first: boolean }) => void;
  renderOrder?: number;
  draggable?: boolean;
}

export const SphereControlHandle: React.FC<SphereControlHandleProps> = ({
  onDrag,
  outline,
  color = 'white',
  hoverCursor = 'pointer',
  size = 0.1,
  onClick,
  renderOrder = 1008,
  draggable = true,
  ...restProps
}) => {
  const [hover, setHover] = useState(false);
  const { controls, camera, pointer, raycaster } = useThree();
  const bind = useDrag(({ event, down, last, first }) => {
    event.stopPropagation();
    const plane = new Plane(new Vector3(0, 1, 0), 0);
    const pos = new Vector3();
    raycaster.setFromCamera(pointer, camera);
    raycaster.ray.intersectPlane(plane, pos);
    onDrag?.({ position: new Vector2(pos.x, pos.z), last: last, first: first });

    if (controls) {
      (controls as any).enabled = !down;
    }
  });

  useCursor(hover, hoverCursor);

  const handleClick = useEventCallback((evt: ThreeEvent<MouseEvent>) => {
    onClick?.(evt);
  });

  const handlePointerOver = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    evt.stopPropagation();
    setHover(true);
  });

  const handlePointerOut = useEventCallback((evt: ThreeEvent<PointerEvent>) => {
    evt.stopPropagation();
    setHover(false);
  });

  return (
    <group renderOrder={renderOrder}>
      <mesh
        {...((draggable ? bind() : {}) as any)}
        {...restProps}
        onClick={handleClick}
        onPointerOut={handlePointerOut}
        onPointerOver={handlePointerOver}
      >
        <sphereGeometry args={[size, 16, 16]} />
        <meshBasicMaterial color="white" depthTest={false} />
      </mesh>
      {outline && (
        <mesh {...((draggable ? bind() : {}) as any)}>
          <ringGeometry args={[size, size * 1.6]} />
          <meshBasicMaterial color={color} depthTest={false} />
        </mesh>
      )}
    </group>
  );
};
